import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, Output, OnInit, HostBinding } from '@angular/core';
import { FormGroup, FormGroupDirective, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { AppSettings } from 'app/modules/shared/app.settings';
import type { Country } from 'app/modules/shared/models/country';
import { CommonBindingDataService } from 'app/modules/shared/services/common-binding-data.service';
import { ConfigService } from 'app/modules/shared/services/config.service';
import { EntityService } from 'app/modules/shared/services/entity.service';
import { CheckboxModule } from 'primeng/checkbox';
import { DividerModule } from 'primeng/divider';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { UserManagementService } from 'app/modules/user-management/services/user-management.service';

interface AutoCompleteCompleteEvent {
  originalEvent: Event;
  query: string;
}

@Component({
  selector: 'app-entity-select-search',
  standalone: true,
  imports: [CommonModule, DropdownModule, FormsModule, AutoCompleteModule, ReactiveFormsModule, TranslateModule, InputTextModule, CheckboxModule, DividerModule, CommonModule],
  templateUrl: './entity-select-search.component.html',
  styleUrl: './entity-select-search.component.scss'
})
export class EntitySelectSearchComponent implements OnDestroy, OnInit {
  checked = [];
  @Input() attributeValues;
  @Input() field: any;
  @Input() attributeLabels: [];
  @Input() moduleName: string;
  @Output() onSearchValueChange: EventEmitter<string> = new EventEmitter();
  formName: FormGroup;
  items: any[] = [];

  selectedItem: any;
  filteredItems: any[] = [];

  @HostBinding('class') get componentClass() {
    return `entity-select-${this.field?.attributeCode || 'default'}`;
  }

  @HostBinding('attr.data-module') get dataModule() {
    return this.moduleName;
  }

  entityData = {
    viewCode: 'AUTOCOMPLETE_VIEW',
    countryCode: '',
    forTenantCode: '',
    searchText: 'a',
    searchStr: '',
    actionStatus: '',
    deleted: '',
    eventCode: '',
    filters: [],
    advanceFilters: []
  };

  country: Country;
  protocolSiteParentChildRelation = ['site_entity_id', 'site_delivery_address_id'];
  placeholder: any;
  entityObject: any = { type: null, id: null };
  entityObjSub: any;
  isInitializing = true;

  constructor(
    private entityService: EntityService,
    private cs: CommonBindingDataService,
    private configService: ConfigService,
    private formGroupDirective: FormGroupDirective,
    private userService: UserManagementService
  ) {
    this.formName = this.formGroupDirective.control;
  }

  ngOnInit() {
    const placeholderLbl = this.field.placeholder || 'Search & Select';
    this.placeholder = this.cs.getLabelValue(`${this.moduleName}.fields.${this.field.attributeCode}.placeholder`, this.attributeLabels, placeholderLbl)
    this.country = JSON.parse(localStorage.getItem(AppSettings.COUNTRY));

    if (this.field.attributeCode === 'user_id' || this.field.attributeCode === 'user_role') {
      this.setupUserFields();
    } else if (this.protocolSiteParentChildRelation.includes(this.field.attributeCode)) {
      this.setupParentChildRelation();
      // this.setupDisabledState();
    } else {
      this.setupRegularField();
    }

    setTimeout(() => {
      this.isInitializing = false;
    }, 100);
  }

  setupDisabledState() {
    const parentChildRelation = this.protocolSiteParentChildRelation;
    const isParentField = this.field.attributeCode === parentChildRelation[0];

    if (isParentField) {
      // this.updateParentDisabledState();

      // this.formName.controls[parentChildRelation[1]].valueChanges.subscribe(() => {
      //   this.updateParentDisabledState();
      // });
    }
  }

  updateParentDisabledState() {
    this.field.disabled = false;
  }

  setupUserFields() {
    if (!this.formName.value[this.field.attributeCode]) {
      this.filteredItems = [];
    }
    this.entityObjSub = this.cs.entityObj$.subscribe(event => {
      this.entityObject = event;
      this.getInitialData();
      if (this.entityObject.id === null) {
        this.selectedItem = null;
        this.filteredItems = [];
      } else {
        const currentValue = this.formName.controls[this.field.attributeCode].value;
        if (currentValue) {
          setTimeout(() => {
            this.setFormValue(currentValue);
          }, 100);
        }
      }
    });
  }

  setupRegularField() {
    this.getInitialData();
    const currentValue = this.formName.controls[this.field.attributeCode].value;
    if (currentValue) {
      setTimeout(() => {
        this.setFormValue(currentValue);
      }, 100);
    }
  }

  setupParentChildRelation() {
    const parentChildRelation = this.protocolSiteParentChildRelation;
    const isChildField = this.field.attributeCode === parentChildRelation[1];
    const isEditMode = this.isEditMode();

    if (isChildField) {
      this.setupChildFieldLogic(parentChildRelation, isEditMode);
    } else {
      this.setupParentFieldLogic(parentChildRelation, isEditMode);
    }

    if (isEditMode && isChildField) {
      this.handleEditModeChildField();
    }
  }

  isEditMode(): boolean {
    const parentChildRelation = this.protocolSiteParentChildRelation;
    const parentValue = this.formName.controls[parentChildRelation[0]]?.value;
    const childValue = this.formName.controls[parentChildRelation[1]]?.value;

    return !!(parentValue && childValue);
  }

  handleEditModeChildField() {
    const currentValue = this.formName.controls[this.field.attributeCode].value;
    if (currentValue) {
      setTimeout(() => {
        this.setFormValue(currentValue);
      }, 500);
    }
  }

  setupChildFieldLogic(parentChildRelation: string[], isEditMode: boolean = false) {
    const parentValue = this.formName.controls[parentChildRelation[0]].value;
    const currentValue = this.formName.controls[this.field.attributeCode].value;

    if (parentValue) {
      this.loadChildData(parentValue);
      if (currentValue) {
        setTimeout(() => {
          this.setFormValue(currentValue);
        }, 200);
      }
    } else {
      if (isEditMode && currentValue) {
        this.filteredItems = [];
        this.selectedItem = null;
        this.loadChildDataForDisabledField(currentValue);
      } else if (!currentValue) {
        this.filteredItems = [];
        this.selectedItem = null;
      } else if (currentValue) {
        this.preserveChildValue(currentValue);
      }
    }

    if (isEditMode && currentValue) {
      setTimeout(() => {
        this.setFormValue(currentValue);
      }, 300);
    }

    let previousParentValue = parentValue;
    let isFirstLoad = true;
    this.formName.controls[parentChildRelation[0]].valueChanges.subscribe(value => {
      if (!this.isInitializing && !isFirstLoad && value !== previousParentValue && !isEditMode) {
        const childValue = this.formName.controls[this.field.attributeCode].value;

        if (!childValue) {
          this.clearChildFieldOnly(parentChildRelation[1]);
          this.selectedItem = null;
          this.filteredItems = [];
          this.items = [];
        }

        if (value) {
          this.loadChildData(value);
        }
      }
      if (isFirstLoad) {
        isFirstLoad = false;
      }
      previousParentValue = value;
    });
  }

  preserveChildValue(currentValue: any) {
    this.entityData.forTenantCode = this.configService.getForTenantCode();
    this.entityData.countryCode = this.country[0].countryCode;
    this.entityData.viewCode = 'AUTOCOMPLETE_VIEW';

    const requestBody = this.buildRequestBody(this.entityData.viewCode, '');
    this.entityService.autoComplete(this.field.moduleName, requestBody).subscribe(res => {
      this.items = this.cs.getTableData(res) || [];
      this.filteredItems = this.items.map(item => ({
        name: item.product_name || item.name || item.site_name || item.delivery_name || item.protocol_name || this.createName(item),
        id: item.id
      }));

      const foundItem = this.filteredItems.find(item => item.id === currentValue);
      if (foundItem) {
        this.selectedItem = foundItem;
      }
    });
  }

  loadChildDataForDisabledField(currentValue: any) {
    this.entityData.forTenantCode = this.configService.getForTenantCode();
    this.entityData.countryCode = this.country[0].countryCode;
    this.entityData.viewCode = 'AUTOCOMPLETE_VIEW';

    const requestBody = this.buildRequestBody(this.entityData.viewCode, '');
    this.entityService.autoComplete(this.field.moduleName, requestBody).subscribe(res => {
      this.items = this.cs.getTableData(res) || [];
      this.filteredItems = this.items.map(item => ({
        name: item.product_name || item.name || item.site_name || item.delivery_name || item.protocol_name || this.createName(item),
        id: item.id
      }));

      const foundItem = this.filteredItems.find(item => item.id === currentValue);
      if (foundItem) {
        this.selectedItem = foundItem;
      } else {
        this.preserveChildValue(currentValue);
      }
    });
  }

  resetChildField(childFieldName: string) {
    const currentValue = this.formName.controls[childFieldName].value;
    if (currentValue) {
      this.formName.controls[childFieldName].setValue(null);
      this.formName.controls[childFieldName].updateValueAndValidity();
    }

    if (this.field.attributeCode === childFieldName) {
      this.selectedItem = null;
      this.filteredItems = [];
      this.items = [];
    }
  }

  setupParentFieldLogic(parentChildRelation: string[], isEditMode: boolean = false) {
    this.getInitialData();

    let previousParentValue = this.formName.controls[parentChildRelation[0]].value;
    let isFirstLoad = true;

    this.formName.controls[parentChildRelation[0]].valueChanges.subscribe(value => {
      if (!this.isInitializing && !isFirstLoad && value !== previousParentValue && !isEditMode) {
        const childField = this.formName.controls[parentChildRelation[1]];
        const childValue = childField.value;

        if (childValue) {
          this.clearChildFieldOnly(parentChildRelation[1]);
        }
      }

      if (isFirstLoad) {
        isFirstLoad = false;
      }
      previousParentValue = value;
    });

    const currentValue = this.formName.controls[this.field.attributeCode].value;
    if (currentValue) {
      setTimeout(() => {
        this.setFormValue(currentValue);
      }, 100);
    }
  }

  clearChildFieldOnly(childFieldName: string) {
    const currentValue = this.formName.controls[childFieldName].value;
    if (currentValue) {
      this.formName.controls[childFieldName].setValue(null);
      this.formName.controls[childFieldName].updateValueAndValidity();
    }
  }

  loadChildData(parentValue: any) {
    if (!parentValue) {
      this.filteredItems = [];
      return;
    }

    this.entityData.forTenantCode = this.configService.getForTenantCode();
    this.entityData.countryCode = this.country[0].countryCode;
    this.entityData.viewCode = 'AUTOCOMPLETE_VIEW';

    const requestBody = this.buildRequestBody(this.entityData.viewCode, '');
    requestBody.filters[0].attributeId = this.field?.filter[0].attributeId;
    requestBody.filters[0].attributeValue = this.formName.controls[this.protocolSiteParentChildRelation[0]].value;
    this.handleGenericEntitySearch(requestBody);

    const currentValue = this.formName.controls[this.field.attributeCode].value;
    if (currentValue) {
      setTimeout(() => {
        this.validateAndSetChildValue(currentValue);
      }, 300);
    }
  }

  validateAndSetChildValue(currentValue: any) {
    const foundItem = this.filteredItems.find(item => item.id === currentValue);
    if (foundItem) {
      this.selectedItem = foundItem;
    } else {
      this.formName.controls[this.field.attributeCode].setValue(null);
      this.formName.controls[this.field.attributeCode].updateValueAndValidity();
      this.selectedItem = null;
    }
  }

  getInitialData() {
    this.entityData.forTenantCode = this.configService.getForTenantCode();
    this.entityData.countryCode = this.country[0].countryCode;
    this.entityData.viewCode = 'AUTOCOMPLETE_VIEW'
    this.fetchFromAPI(this.entityData.viewCode, '');
  }

  setOptions(items: any) {
    this.items = items.map(item => ({
      name: item.product_name || item.name,
      id: item.id
    }));
  }

  filterItems(event: AutoCompleteCompleteEvent) {
    if (this.field?.disabled) {
      return;
    }

    const query = event.query;
    this.entityData.searchText = query;
    this.entityData.searchStr = query;

    if (this.protocolSiteParentChildRelation.includes(this.field.attributeCode) &&
      this.field.attributeCode === this.protocolSiteParentChildRelation[1]) {
      const parentValue = this.formName.controls[this.protocolSiteParentChildRelation[0]].value;
      if (!parentValue) {
        this.filteredItems = [];
        return;
      }
    }

    this.fetchFromAPI(this.entityData.viewCode, query);
  }

  onSelectItem(event) {
    if (this.field?.disabled) {
      return;
    }

    if (event.value) {
      this.selectedItem = event.value;
      this.formName.controls[this.field.attributeCode].setValue(event.value.id);
      this.formName.controls[this.field.attributeCode].updateValueAndValidity();



      if (this.field.attributeCode === 'user_id' || this.field.attributeCode === 'user_role') {
        this.cs.emitUserRole(event.value.id);
      }
    }
  }

  fetchFromAPI(viewCode: string, search: string) {
    this.country = JSON.parse(localStorage.getItem(AppSettings.COUNTRY));
    const requestBody = this.buildRequestBody(viewCode, search);

    if (this.field?.attributeCode === this.protocolSiteParentChildRelation[1]) {
      this.handleProtocolSiteChildRelation(requestBody);
    } else if (this.field.attributeCode === 'user_id') {
      this.handleUserIdSearch();
    } else if (this.field.attributeCode === 'user_role') {
      this.handleUserRoleSearch();
    } else {
      this.handleGenericEntitySearch(requestBody);
    }
  }

  private buildRequestBody(viewCode: string, search: string) {
    const requestBody: any = {
      viewCode,
      countryCode: this.country[0].countryCode,
      forTenantCode: this.configService.getForTenantCode(),
      searchText: search,
      defaultSortType: AppSettings.ASC_SORT_ORDER,
      actionStatus: this.moduleName === 'protocol_product' ? 'UNARCHIVE' : (this.field?.actionStatus || ''),
      deleted: AppSettings.DELETED_TYPE.ONLY_NON_DELETED,
      filters: this.field?.filter ? [...this.field.filter] : [],
      advanceFilters: []
    };

    if (this.field.attributeCode === 'site_delivery_address_id') {
      requestBody.filters[0].attributeId = this.field?.filter[0].attributeId;
      requestBody.filters[0].attributeValue = this.formName.controls[this.protocolSiteParentChildRelation[0]].value;
    }

    if (this.field?.defaultSortColumn) {
      requestBody.defaultSortColumn = this.field?.defaultSortColumn;
    }


    if (!requestBody.filters) {
      requestBody.filters = [];
    }

    return requestBody;
  }

  private handleProtocolSiteChildRelation(requestBody: any) {
    requestBody.filters = this.field?.filter && this.field?.filter;
    requestBody.filters[0].attributeId = this.field?.filter[0].attributeId;
    requestBody.filters[0].attributeValue = this.formName.controls[this.protocolSiteParentChildRelation[0]].value;
    this.handleGenericEntitySearch(requestBody);
  }

  private handleUserIdSearch() {
    if (this.entityObject?.id) {
      (this.entityData as any).userType = [this.entityObject.type];
      (this.entityData as any).typeId = [this.entityObject.id];
      (this.entityData as any).status = 'Active';
      this.userService.searchEntityUser(this.entityData).subscribe((res: any) => {
        if (res.data?.length) {
          this.filteredItems = res.data?.map(item => ({
            name: `${item?.firstName} ${item?.lastName}`,
            id: item.userId
          }));
          if (this.formName.value[this.field.attributeCode]) {
            this.selectedItem = this.filteredItems.filter(ele => ele.id === this.formName.value[this.field.attributeCode])[0];
          }
        } else {
          this.filteredItems = [];
        }
      });
    } else {
      this.filteredItems = [];
    }
  }

  private handleUserRoleSearch() {
    if (this.entityObject.type) {
      this.selectedItem = null;
      this.filteredItems = [];
      this.field.presetValues.forEach(ele => {
        if (ele.parentLabelKey === this.entityObject.type) {
          this.filteredItems.push(
            { name: ele.labelValue, id: ele.labelKey }
          );
          if (this.formName.value[this.field.attributeCode]) {
            this.selectedItem = this.filteredItems.filter(ele => ele.id === this.formName.value[this.field.attributeCode])[0];
            if (this.selectedItem) {
              this.cs.emitUserRole(this.selectedItem?.id);
            }
          }
        }
      });
    } else {
      this.filteredItems = [];
    }
  }

  private handleGenericEntitySearch(requestBody: any) {
    this.entityService.autoComplete(this.field.moduleName, requestBody).subscribe(res => {
      this.items = this.cs.getTableData(res) || [];
      this.filteredItems = this.items.map(item => ({
        name: item.product_name || item.name || item.site_name || item.delivery_name || item.protocol_name || this.createName(item) || item.sponsor_name,
        id: item.id
      })).sort((a, b) => a.name.localeCompare(b.name));

      const currentValue = this.formName.value[this.field.attributeCode];
      if (currentValue && !this.selectedItem) {
        const foundItem = this.filteredItems.find(item => item.id === currentValue);
        if (foundItem) {
          this.selectedItem = foundItem;
        }
      }
    });
  }



  createName(item) {
    const parts = [
      item.address_short_name,
      item.address_line_1,
      item.address_line_2,
      item.street,
      item.city,
      item.state,
      item.zip
    ].filter(part => part && part.trim());
    return parts.join(', ');
  }

  setFormValue(value: any) {
    if (typeof value === 'object' && value.id) {
      this.formName.controls[this.field.attributeCode].setValue(value.id);
      this.formName.controls[this.field.attributeCode].updateValueAndValidity();
      this.selectedItem = value;
    } else if (typeof value === 'string' || typeof value === 'number') {
      this.formName.controls[this.field.attributeCode].setValue(value);
      this.formName.controls[this.field.attributeCode].updateValueAndValidity();

      const foundItem = this.filteredItems.find(item => item.id === value);
      if (foundItem) {
        this.selectedItem = foundItem;
      }
    }
  }

  onClear() {
    this.selectedItem = null;
    this.formName.controls[this.field.attributeCode].setValue(null);
    this.formName.controls[this.field.attributeCode].updateValueAndValidity();

    this.entityData.searchText = '';
    this.entityData.searchStr = '';

    this.fetchFromAPI(this.entityData.viewCode, '');
  }

  ngOnDestroy(): void {
    if (this.field.attributeCode === 'user_role') {
      this.cs.emitUserRole(null);
    }
    if (this.entityObjSub) {
      this.entityObjSub.unsubscribe();
    }
  }
}
